<template>
  <Page :title="pageTitle" id="project">
    <div v-if="isLoading" class="project-loading" aria-live="polite">
      <LoadingIndicator message="Loading project…" />
    </div>

    <div class="is-flex" v-if="project">
      <div class="project-tabs" v-if="canViewBidPackage">
        <Tabs :bid-package-id="viewBidPackageId" @projectBidderUpdated="handleProjectBidderUpdated" />
      </div>
      <div v-else>
        <BidPackagesList layout-type="landing" />
      </div>
    </div>

    <o-modal
        v-model:active="isConfirmLeaveBidding"
        :width="600"
        content-class="bidding-confirmation-modal"
    >
      <div class="title">Will You Be Bidding?</div>
      <div>You are leaving without letting us know if you are bidding or not.</div>
      <div>Will you be bidding?</div>

      <div class="has-text-right">
        <o-button class="is-danger has-margin-right-large" @click="handleBidding('no')">No</o-button>
        <o-button class="is-success" @click="handleBidding('yes')">Yes</o-button>
      </div>
    </o-modal>

 </Page>
</template>

<script setup lang="ts">

import {onBeforeRouteLeave, useRoute, useRouter} from "vue-router";
import { storeToRefs } from 'pinia';
import {DateTime} from 'luxon';

import Page from "@/components/Page.vue";
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import Tabs from "./Tabs.vue";

import {
  NotificationError,
  NotificationSuccess,
} from "@/internal";
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import BidPackagesList from "@/pages/Projects/BidPackagesList.vue";
import type {BidPackage} from "@/models/BidPackage";
import {useBidPackageProjectBidderStore} from "@/stores/BidPackageProjectBidderStore";
import {useProjectStore} from "@/stores/ProjectStore";
import {parseRFQDateTime} from '@/use/formatRFQDateTime';

const bidPackageProjectBidderStore = useBidPackageProjectBidderStore();
const projectStore = useProjectStore();
const route = useRoute();
const router = useRouter();
const { project, viewBidPackageId } = storeToRefs(useProjectStore());
const goToRoute = ref<any | null>(null);
const isConfirmLeaveBidding = ref<boolean>(false);
const interval = ref<number | null>(null);
const preserveProjectState = ref(false);

const biddingNotAnswered = computed(() => {
  return (isOpenProjectAndUninvited.value && !project.value?.bidders.length && !project.value?.bid_packages.length)
      || (project.value?.bidders.length && project.value?.is_bidding === 0)
      || (project.value?.bid_packages.length && project.value?.bid_packages.filter((bidPackage: BidPackage) => bidPackage.is_bidding === 0).length);
});

const isLoading = computed(() => projectStore.isLoading);

const canViewBidPackage = computed(() => {
  if (!project.value?.bid_packages.length) {
    return viewBidPackageId.value === 0;
  }

  return viewBidPackageId.value !== null
    && Boolean(project.value?.bid_packages.some((bidPackage: BidPackage) => bidPackage.id === viewBidPackageId.value));
});

const isOpenProjectAndUninvited = computed(() => project.value?.open_invitation && !project.value.invited);

const pageTitle = computed(() => {
  const projectName = project.value?.name?.trim() ?? '';
  const bidPackage = project.value?.bid_packages.find(
    (item: BidPackage) => item.id === viewBidPackageId.value,
  );
  const bidPackageName = bidPackage?.name?.trim();

  if (!bidPackageName) {
    return projectName;
  }

  const bidPhaseName = project.value?.bid_phase_name?.trim();
  const projectContext = bidPhaseName
    ? `${projectName} (${bidPhaseName})`
    : projectName;

  return projectContext
    ? `${projectContext} – ${bidPackageName}`
    : bidPackageName;
});

const projectRouteId = computed(() => String(route.params.projectId ?? ''));
const projectId = computed(() => Number(projectRouteId.value));
const isPastProjectRoute = computed(() => String(route.name).startsWith('projects/past'));
const routeBidPackageId = computed(() => {
  const value = route.params.bidPackageId;
  return typeof value === 'string' ? parseInt(value) : null;
});

onMounted(async () => {
  if (String(project.value?.id) !== projectRouteId.value) {
    projectStore.clear();
  }

  projectStore.setViewBidPackageId(routeBidPackageId.value);
  await loadProject();
});

onBeforeRouteLeave(async (to, from) => {
  const remainsWithinProject = String(to.params.projectId ?? '') === String(from.params.projectId ?? '');
  preserveProjectState.value = remainsWithinProject;

  // A package is part of the current project flow. Do not interrupt a
  // subcontractor who is entering or switching bid packages; the response
  // reminder is for exits back to a project list or another project.
  if (remainsWithinProject) {
    return true;
  }

  if (goToRoute.value) {
    return true;
  }

  goToRoute.value = to;
  const subBidDue = parseRFQDateTime(project.value?.sub_bid_due);
  if (biddingNotAnswered.value && subBidDue && subBidDue > DateTime.local()) {
    isConfirmLeaveBidding.value = true;

    interval.value = setInterval((to: any) => {
      if (!biddingNotAnswered.value || !isConfirmLeaveBidding.value) {
        if (interval.value) {
          clearInterval(interval.value);
          interval.value = null;
        }
        router.push(to);
      }
    }, 1000, to);

    return false;
  }
});

onBeforeUnmount(() => {
  if (!preserveProjectState.value) {
    projectStore.setViewBidPackageId(null);
    bidPackageProjectBidderStore.clearBidder();
    bidPackageProjectBidderStore.clearBidders();
  }

  if (interval.value) {
    clearInterval(interval.value);
    interval.value = null;
  }
});

function handleBidding(answer: 'yes' | 'no') {
  if (!['yes', 'no'].includes(answer)) {
    return;
  }

  const payload: any = {
    confirmed: answer === 'yes' ? 1 : 2,
  };

  bidPackageProjectBidderStore.updateUnconfirmed(<number>project.value?.id, payload)
    .then(() => {
      NotificationSuccess({
        message: `Response Updated`,
      });

      handleProjectBidderUpdated();
    }).catch((error: any) => {
      if (error.errors) {
        NotificationError({}, error);
      }
      else {
        NotificationError({
          message: error,
        });
      }
    }).finally(() => {
      isConfirmLeaveBidding.value = false;
    });
}

function handleProjectBidderUpdated() {
  loadProject();
  loadBidPackageProjectBidders();
}

function loadBidPackageProjectBidders() {
  const viewingProjectLevelInvites = viewBidPackageId.value === 0 && Boolean(project.value?.bidders.length);
  const selectedBidPackageIsAvailable = project.value?.bid_packages.some(
    (bidPackage: BidPackage) => bidPackage.id === viewBidPackageId.value,
  );

  if ((viewBidPackageId.value === null) || !projectId.value || (!viewingProjectLevelInvites && !selectedBidPackageIsAvailable)) {
    return;
  }

  const payload = {
    params: {
      include: [
          'trade',
      ],
    }
  };

  bidPackageProjectBidderStore.index(projectId.value, viewBidPackageId.value, payload)
    .catch(error => {
      if (error.errors) {
        NotificationError({}, error);
      } else {
        NotificationError({
          message: error,
        });
      }
    });
}

function loadProject() {
  const payload = {
    params: {
      include: [
        'bidders.trade',
        'bidPackages',
        'estimators',
        'notices',
        'projectContacts',
      ],
    }
  };

  return projectStore.show(projectId.value, payload)
      .then(() => {
        if (redirectToSecureAccess()) {
          return;
        }

        if (redirectFromInaccessibleBidPackage()) {
          return;
        }

        if (!project.value?.bid_packages.length && (project.value?.bidders.length || (project.value?.open_invitation && !project.value?.invited))) {
          projectStore.setViewBidPackageId(0);
        }

        // A direct bid-package URL sets the selected package before the project
        // has loaded. In that case the watcher below has nothing new to react
        // to, so explicitly load the package's bidder responses once the
        // project and its accessible packages are available.
        loadBidPackageProjectBidders();
      }).catch(error => {
        if (error?.code === 'NDA_REQUIRED' && error.nda?.id) {
          window.sessionStorage.setItem('pendingNda', JSON.stringify({
            projectId: projectId.value,
            nda: error.nda,
            returnPath: route.fullPath,
          }));
          router.replace({ name: 'nda' });
          return;
        }

        if (error === 'This project is no longer available to your company.') {
          projectStore.clear();
          NotificationError({ message: error });
          router.replace({ name: isPastProjectRoute.value ? 'projects/past' : 'projects/current' });
          return;
        }

        if (error.errors) {
          NotificationError({}, error);
        } else {
          NotificationError({
            message: error,
          });
        }
      });

}

function redirectToSecureAccess(): boolean {
  if (!project.value?.is_secure_project || project.value.has_secure_access) {
    return false;
  }

  router.replace({
    name: isPastProjectRoute.value ? 'projects/past/request-access' : 'projects/request-access',
    params: {projectId: projectRouteId.value},
  });

  return true;
}

function redirectFromInaccessibleBidPackage(): boolean {
  if (routeBidPackageId.value === null) {
    return false;
  }

  const packageIsAvailable = project.value?.bid_packages.some(
    (bidPackage: BidPackage) => bidPackage.id === routeBidPackageId.value,
  );

  if (packageIsAvailable) {
    return false;
  }

  projectStore.setViewBidPackageId(null);
  router.replace({
    name: isPastProjectRoute.value ? 'projects/past/show' : 'projects/show',
    params: { projectId: projectRouteId.value },
    hash: route.hash,
  });

  return true;
}

watch(viewBidPackageId, () => {
  loadBidPackageProjectBidders();
});

watch(routeBidPackageId, (bidPackageId) => {
  if (bidPackageId === null && !project.value?.bid_packages.length && project.value?.bidders.length) {
    projectStore.setViewBidPackageId(0);
    return;
  }

  projectStore.setViewBidPackageId(bidPackageId);
});

watch(isConfirmLeaveBidding, () => {
  if (!isConfirmLeaveBidding.value && biddingNotAnswered.value && interval.value) {
    clearInterval(interval.value);
    interval.value = null;
    router.push(goToRoute.value);
  }
});

</script>

<style lang="scss" scoped>

#project {

  .project-loading {
    align-items: center;
    background: rgba(255, 255, 255, .75);
    display: flex;
    inset: 0;
    justify-content: center;
    position: fixed;
    z-index: 10;
  }

  .bid-packages {
    width: 250px;

    .bid-packages-list {
      height: 78vh;
      overflow-y: scroll;
      margin-top: 16px;
    }
  }

  .project-tabs {
    padding-top: 0;
    margin-left: -1px;
    width: 100%;
  }

}

</style>

<style lang="scss">

#project {

  .b-tabs {
    .tabs {
      width: 100%;
      white-space: normal;
    }
  }
}

.b-tabs {
  a {
    color: $link !important;

    &.is-active {
      color: $text !important;
      font-weight: 500;
    }
  }
}

.modal .animation-content.bidding-confirmation-modal {
  padding: 1.25rem;
}

@media screen and (max-width: 768px) {
  .modal .animation-content.bidding-confirmation-modal {
    box-sizing: border-box;
    width: calc(100vw - 2rem);
    max-height: calc(100vh - 2rem); // exists for compatibility support, typically ignored by modern browsers
    max-height: calc(100dvh - 2rem);
    margin: 1rem;
    padding: 1.25rem;

    .title {
      font-size: 1.5rem;
      line-height: 1.25;
    }
  }
}

</style>
